import {isNotNil} from 'ramda';

import {getCustomerName} from '@dms/shared';

import {
  ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS,
  DEFAULT_INTEREST_SIGNAL_POLICY,
  detectInterestSignals,
  type AssistantInterestContext,
} from '@prompt-bridge/shared/ai-assistant-core';

import {EMPTY_PLACEHOLDER} from 'shared/web';

import type {ToSalesInterestAssistantContextInput} from '../types/ToSalesInterestAssistantContextInput';
import {resolveAssistantInterestLanguage} from './resolveAssistantInterestLanguage';
import {
  toAssistantInterestActivityMessages,
  toAssistantInterestCustomerMessages,
  toAssistantInterestTimeline,
} from './toAssistantInterestTimeline';
import {toBoundedAssistantInterestText} from './toBoundedAssistantInterestText';
import {toSalesInterestFacts} from './toSalesInterestFacts';
import {toSalesInterestOrigin} from './toSalesInterestOrigin';
import {toSalesInterestVehicles} from './toSalesInterestVehicles';

export function toSalesInterestAssistantContext(
  input: ToSalesInterestAssistantContextInput
): AssistantInterestContext {
  const facts = toSalesInterestFacts(input);
  const policy = input.policy ?? DEFAULT_INTEREST_SIGNAL_POLICY;
  const timeline = toAssistantInterestTimeline(
    facts.timeline,
    new Map([
      ...toAssistantInterestActivityMessages(input.activities),
      ...toAssistantInterestCustomerMessages(input.customerMessages),
    ])
  );
  const customerName = getCustomerName(input.customer);
  const displayName =
    customerName === EMPTY_PLACEHOLDER ? null : toBoundedAssistantInterestText(customerName);
  const language = resolveAssistantInterestLanguage(
    input.customer.preferredLocale,
    input.tenant?.defaultLocale ?? null,
    input.tenant?.availableLocales ?? []
  );
  const publicId = toBoundedAssistantInterestText(input.interest.publicId);
  const branchId = toBoundedAssistantInterestText(input.interest.branchId);
  const interestType = toBoundedAssistantInterestText(input.interest.type);
  const origin = toSalesInterestOrigin(input);

  return {
    kind: facts.kind,
    interestId: input.interestId.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
    ...(isNotNil(branchId) ? {branchId} : {}),
    ...(isNotNil(publicId) ? {publicId} : {}),
    state: facts.state,
    ...(isNotNil(interestType) ? {interestType} : {}),
    createdAt: facts.createdAt,
    updatedAt: facts.updatedAt,
    firstResponseAt: facts.firstResponseAt,
    leadQuality: facts.leadQuality,
    ...(isNotNil(origin) ? {origin} : {}),
    customer: {
      id: input.customer.id.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
      ...(isNotNil(displayName) ? {displayName} : {}),
      ...language,
    },
    ...(isNotNil(input.sender) ? {sender: input.sender} : {}),
    vehiclesOfInterest: toSalesInterestVehicles(input),
    timeline,
    plannedActivities: facts.plannedActivities.map((activity) => {
      const task = input.upcomingTasks.find((candidate) => candidate.id === activity.id);
      const title = toBoundedAssistantInterestText(task?.title);
      return {
        ...activity,
        id: activity.id.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
        ...(isNotNil(title) ? {title} : {}),
      };
    }),
    completeness: facts.completeness,
    signals: detectInterestSignals({facts, policy, evaluatedAt: input.evaluatedAt}),
    policy: {
      coldThresholdDays: policy.coldThresholdDays,
      awaitingReplyMinimumHours: policy.awaitingReplyMinimumHours,
      noFirstResponseMinimumHours: policy.noFirstResponseMinimumHours,
    },
    evaluatedAt: input.evaluatedAt,
  };
}
